import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  IconAudioRecord,
  IconArrowUpRight,
  IconChevronV3,
  IconBag,
  IconCheck,
  IconClose,
  IconDashboard,
  IconLoading,
  IconMail,
  IconSupport,
  IconAdd,
  IconSites,
  IconTools,
} from 'gatorcomponents';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import useLocale from '@/hooks/useLocale';
import { useResize } from '@/hooks/useResize';
import { useGatorChat } from '@/hooks/useGatorChat';
import { homeActions } from '@/redux/modules';
import { loadPendingTicketsV2 } from '@/redux/actions/tickets';
import {
  ATIVACAO_ENABLE_AI_ALERTS,
  OPERACOES_ENABLE_SUPPORT_TICKETS_CTA,
} from '@/config/GrowthBook/constants';
import { OPTIONS } from '@/components/Layout/Menu/HeaderV2/components/NewAiHeaderButton/NewAiHeaderButton.types';
import GatorPresentationModal from '@/components/Modal/GatorPresentationModal';
import gatorAvatar from '@/media/gatorAi/gator-32.svg';
import gatorRecordingAvatar from '@/media/gatorAi/gator-recording.png';
import type { GatorCompactHeaderProps, NotificationVariant } from './GatorCompactHeader.types';
import * as Styles from './GatorCompactHeader.styles';

type SummaryState = { summary: { firstname?: string } };

type GatorAlert = { type?: { slug?: string } };
type GatorState = {
  merlin?: {
    alerts?: GatorAlert[];
  };
};

type TicketsState = {
  tickets?: { pending?: { ticketsV2?: unknown[] } };
};

type ChipId = 'site' | 'email' | 'marketing' | 'support' | 'domainPurchase' | 'domainConfig' | 'checkAccount' | 'contractProduct';
type ExpandedChipId = 'domainPurchase' | 'domainConfig' | 'checkAccount' | 'contractProduct';

type IconSize = 'md' | 'sm' | 'sm2' | 'lg' | 'xl';

type ChipConfig = {
  id: ChipId;
  action: string;
  optionLabel: string;
  Icon: React.ComponentType<{ size?: IconSize; color?: string }>;
  hideOnMobile?: boolean;
  spanFull?: boolean;
};

type NotificationContent = { tag: string; text: string; textShort?: string };

type AichatLocale = {
  aichat?: {
    compactHeader?: {
      greeting: (_name: string) => string;
      greetingNoName: string;
      placeholder: string;
      newGatorTag: string;
      notificationText: string;
      notificationTextShort?: string;
      notificationAlerts?: NotificationContent;
      notificationTickets?: NotificationContent;
      audio?: { recordingLabel: string; transcribingLabel: string };
      chips?: Record<'site' | 'email' | 'marketing' | 'support', { label: string; message: string }>;
      suggestionsLabel?: string;
      suggestions?: string[];
      expandedChips?: Record<ExpandedChipId, { label: string; message: string }>;
      learnMoreLabel?: string;
    };
  };
  routes?: { help: string };
};

const CHIPS_COLLAPSED: ChipConfig[] = [
  {
    id: 'site', action: 'newSite', optionLabel: OPTIONS.CREATE_SITE, Icon: IconDashboard,
  },
  {
    id: 'email', action: 'email', optionLabel: OPTIONS.CREATE_EMAIL, Icon: IconMail, hideOnMobile: true,
  },
  {
    id: 'support', action: 'diagnostic', optionLabel: OPTIONS.SUPPORT, Icon: IconSupport,
  },
];

const CHIPS_EXPANDED: ChipConfig[] = [
  {
    id: 'site', action: 'newSite', optionLabel: OPTIONS.CREATE_SITE, Icon: IconDashboard,
  },
  {
    id: 'email', action: 'email', optionLabel: OPTIONS.CREATE_EMAIL, Icon: IconMail,
  },
  {
    id: 'support', action: 'diagnostic', optionLabel: OPTIONS.SUPPORT, Icon: IconSupport,
  },
  {
    id: 'domainPurchase', action: 'domainPurchase', optionLabel: OPTIONS.DOMAIN_PURCHASE, Icon: IconAdd,
  },
  {
    id: 'domainConfig', action: 'domainConfig', optionLabel: OPTIONS.CONFIGURE_DOMAIN, Icon: IconSites,
  },
  {
    id: 'checkAccount', action: 'checkAccount', optionLabel: OPTIONS.CHECK_ACCOUNT, Icon: IconTools,
  },
  {
    id: 'contractProduct', action: 'contractProduct', optionLabel: OPTIONS.CONTRACT_PRODUCT, Icon: IconBag, spanFull: true,
  },
];

const GatorCompactHeader = ({ name, placeholder }: GatorCompactHeaderProps) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const reduxFirstname = useSelector((state: SummaryState) => state.summary.firstname);
  const gatorAlerts = useSelector((state: GatorState) => state.merlin?.alerts);
  const openTickets = useSelector((state: TicketsState) => state.tickets?.pending?.ticketsV2 ?? []);

  const { aichat, routes } = useLocale() as AichatLocale;
  const { width: viewportWidth } = useResize();
  const isMobileView = viewportWidth <= 600;
  const withAiAlerts = useFeatureIsOn(ATIVACAO_ENABLE_AI_ALERTS);
  const enableSupportTicketsCta = useFeatureIsOn(OPERACOES_ENABLE_SUPPORT_TICKETS_CTA);

  const {
    handleSubmit,
    handleChipClick,
    handleSuggestionPromptClick,
    handleOpenAlerts,
    canShowAudio,
    isRecording,
    loadingTranscription,
    handleStartRecording,
    handleStopRecording,
    formattedTime,
    chatIsOpen,
  } = useGatorChat();

  const [value, setValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState(false);

  const aiWrapperRef = useRef<HTMLDivElement>(null);
  const [storedRef, setStoredRef] = useState<{ offsetLeft: number; offsetTop: number; offsetWidth: number; offsetHeight: number } | null>(null);

  const effectiveName = name ?? reduxFirstname;
  const greetingText = effectiveName
    ? aichat?.compactHeader?.greeting(effectiveName)
    : aichat?.compactHeader?.greetingNoName;

  const hasActiveAlerts = withAiAlerts && Array.isArray(gatorAlerts) && gatorAlerts.length > 0;
  const hasOpenTickets = enableSupportTicketsCta && openTickets.length > 0;

  const getNotificationVariant = (): NotificationVariant => {
    if (hasOpenTickets) return 'tickets';
    if (hasActiveAlerts) return 'alerts';
    return 'default';
  };
  const notificationVariant = getNotificationVariant();

  const notificationContent: Record<NotificationVariant, { tag?: string; text?: string; textShort?: string }> = {
    default: {
      tag: aichat?.compactHeader?.newGatorTag,
      text: aichat?.compactHeader?.notificationText,
      textShort: aichat?.compactHeader?.notificationTextShort,
    },
    alerts: {
      tag: aichat?.compactHeader?.notificationAlerts?.tag,
      text: aichat?.compactHeader?.notificationAlerts?.text,
      textShort: aichat?.compactHeader?.notificationAlerts?.textShort,
    },
    tickets: {
      tag: aichat?.compactHeader?.notificationTickets?.tag,
      text: aichat?.compactHeader?.notificationTickets?.text,
    },
  };

  useEffect(() => {
    if (enableSupportTicketsCta) {
      dispatch(loadPendingTicketsV2());
    }
  }, [enableSupportTicketsCta, dispatch]);

  const updateAiWrapperRef = useCallback(() => {
    if (!aiWrapperRef.current) return;
    const {
      offsetLeft, offsetTop, offsetWidth, offsetHeight,
    } = aiWrapperRef.current;
    const data = {
      offsetLeft, offsetTop, offsetWidth, offsetHeight,
    };
    if (!storedRef || storedRef.offsetHeight !== data.offsetHeight) {
      setStoredRef(data);
      dispatch(homeActions.setHomeAiWrapperRef({ aiWrapperRef: data }));
    }
  }, [dispatch, storedRef]);

  useEffect(() => {
    updateAiWrapperRef();
  }, [updateAiWrapperRef]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    if (value.trim().length === 0) {
      setIsExpanded(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    event.preventDefault();
    handleSubmit(trimmed);
    setValue('');
    setIsExpanded(false);
  };

  const handleNotificationClick = () => {
    if (notificationVariant === 'tickets') {
      history.push(routes?.help ?? '');
      return;
    }
    if (notificationVariant === 'alerts') {
      handleOpenAlerts();
      return;
    }
    setIsPresentationModalOpen(true);
  };

  return (
    <Styles.Container ref={aiWrapperRef} data-testid="gator-compact-header">
      <Styles.WhiteBg />
      <Styles.VersionTag data-testid="gator-compact-header-version-tag">
        <Styles.VersionTagText>2.0</Styles.VersionTagText>
      </Styles.VersionTag>
      <Styles.HeaderRow>
        <Styles.Avatar>
          <img src={gatorAvatar} alt="" />
        </Styles.Avatar>
        <Styles.GreetingText data-testid="gator-compact-header-greeting">
          {greetingText}
        </Styles.GreetingText>
      </Styles.HeaderRow>
      <Styles.CompactInputWrapper
        $isExpanded={isExpanded}
        data-expanded={isExpanded}
        data-testid="gator-compact-header-wrapper"
      >
        {isRecording ? (
          <Styles.RecordingContent data-testid="gator-compact-header-recording">
            <Styles.RecordingLeft>
              <Styles.RecordingAvatar>
                <img src={gatorRecordingAvatar} alt="" />
              </Styles.RecordingAvatar>
              <Styles.RecordingLabel>
                {loadingTranscription
                  ? aichat?.compactHeader?.audio?.transcribingLabel
                  : aichat?.compactHeader?.audio?.recordingLabel}
              </Styles.RecordingLabel>
            </Styles.RecordingLeft>
            <Styles.RecordingRight>
              <Styles.RecordingTime data-testid="gator-compact-header-recording-time">
                {formattedTime}
              </Styles.RecordingTime>
              <Styles.RecordingActionButton
                type="button"
                $variant="cancel"
                onClick={() => handleStopRecording({ cancelRecording: true })}
                disabled={loadingTranscription}
                data-testid="gator-compact-header-recording-cancel"
              >
                <IconClose size="sm" color="highPure" />
              </Styles.RecordingActionButton>
              <Styles.RecordingActionButton
                type="button"
                $variant="send"
                onClick={() => handleStopRecording({ cancelRecording: false })}
                disabled={loadingTranscription}
                data-testid="gator-compact-header-recording-send"
              >
                {loadingTranscription ? <IconLoading size="sm" /> : <IconCheck size="sm" color="highPure" />}
              </Styles.RecordingActionButton>
            </Styles.RecordingRight>
          </Styles.RecordingContent>
        ) : (
          <>
            <Styles.CompactInputField
              rows={1}
              $isExpanded={isExpanded}
              value={value}
              placeholder={placeholder ?? aichat?.compactHeader?.placeholder ?? ''}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={chatIsOpen}
              data-testid="gator-compact-header-input"
            />
            {canShowAudio && (
              <Styles.AudioButton
                type="button"
                onClick={handleStartRecording}
                $isExpanded={isExpanded}
                data-testid="gator-compact-header-audio-button"
              >
                <IconAudioRecord size="sm2" color="lowMedium" />
              </Styles.AudioButton>
            )}
          </>
        )}
      </Styles.CompactInputWrapper>
      <Styles.NotificationBar
        type="button"
        $variant={notificationVariant}
        onClick={handleNotificationClick}
        data-testid="gator-compact-header-notification-bar"
      >
        <Styles.NotificationTextGroup>
          <Styles.NewGatorTag $variant={notificationVariant}>
            <Styles.NewGatorTagText $variant={notificationVariant}>
              {notificationContent[notificationVariant].tag}
            </Styles.NewGatorTagText>
          </Styles.NewGatorTag>
          <Styles.NotificationText
            $hasShortText={Boolean(notificationContent[notificationVariant].textShort)}
          >
            {notificationContent[notificationVariant].text}
          </Styles.NotificationText>
          {notificationContent[notificationVariant].textShort && (
            <Styles.NotificationTextShort>
              {notificationContent[notificationVariant].textShort}
            </Styles.NotificationTextShort>
          )}
        </Styles.NotificationTextGroup>
        <IconChevronV3 direction="right" size="sm" color="primaryExtraDark" />
      </Styles.NotificationBar>
      <Styles.SuggestedOptions $showMore={showMore}>
        {(showMore ? CHIPS_EXPANDED : CHIPS_COLLAPSED).map((chip) => {
          const chipLocale = (['site', 'email', 'marketing', 'support'] as ChipId[]).includes(chip.id)
            ? aichat?.compactHeader?.chips?.[chip.id as 'site' | 'email' | 'marketing' | 'support']
            : aichat?.compactHeader?.expandedChips?.[chip.id as ExpandedChipId];
          return (
            <Styles.SuggestionChip
              key={chip.id}
              type="button"
              onClick={() => handleChipClick({ action: chip.action, optionLabel: chip.optionLabel }, chipLocale?.message)}
              disabled={chatIsOpen}
              $hideOnMobile={chip.hideOnMobile}
              $spanFull={chip.spanFull}
              data-testid={`gator-compact-header-chip-${chip.id}`}
            >
              <chip.Icon color={isMobileView ? 'highPure' : 'highDark'} />
              <Styles.SuggestionChipLabel>
                {chipLocale?.label}
              </Styles.SuggestionChipLabel>
            </Styles.SuggestionChip>
          );
        })}
        {!showMore && (
          <Styles.SuggestionChipPlus
            type="button"
            onClick={() => setShowMore(prev => !prev)}
            data-testid="gator-compact-header-chip-plus"
          >
            <IconAdd color={isMobileView ? 'highPure' : 'highDark'} />
          </Styles.SuggestionChipPlus>
        )}
      </Styles.SuggestedOptions>
      {showMore && (
        <Styles.SuggestionsList data-testid="gator-compact-header-suggestions">
          <Styles.SuggestionsHeader>
            {aichat?.compactHeader?.suggestionsLabel}
          </Styles.SuggestionsHeader>
          {(aichat?.compactHeader?.suggestions ?? []).map((item, index) => (
            <React.Fragment key={item}>
              {index > 0 && <Styles.SuggestionDivider />}
              <Styles.SuggestionItem
                type="button"
                onClick={() => handleSuggestionPromptClick(item)}
                disabled={chatIsOpen}
                data-testid={`gator-compact-header-suggestion-${index}`}
              >
                <Styles.SuggestionItemText>{item}</Styles.SuggestionItemText>
                <IconArrowUpRight
                  size="sm"
                  color={isMobileView ? 'highPure' : 'highDark'}
                  backgroundColor={isMobileView ? 'transparent' : undefined}
                />
              </Styles.SuggestionItem>
            </React.Fragment>
          ))}
          {(aichat?.compactHeader?.suggestions?.length ?? 0) > 0 && <Styles.SuggestionDivider />}
          <Styles.LearnMoreButton
            type="button"
            onClick={() => setIsPresentationModalOpen(true)}
            data-testid="gator-compact-header-learn-more"
          >
            {aichat?.compactHeader?.learnMoreLabel}
            <IconChevronV3 direction="right" size="sm" color={isMobileView ? 'highPure' : 'primaryMedium'}/>
          </Styles.LearnMoreButton>
        </Styles.SuggestionsList>
      )}
      <GatorPresentationModal
        open={isPresentationModalOpen}
        onClose={() => setIsPresentationModalOpen(false)}
      />
    </Styles.Container>
  );
};

export default GatorCompactHeader;
